import React, { useState, useEffect, useContext } from 'react';
import styled from '@emotion/styled';
import { Link, useStaticQuery, graphql } from 'gatsby';
import {
  theme,
  siteHeaderTopSafePadding,
  SITE_HEADER_TOP_MIN_PADDING_PX,
  MOBILE_LOGO_ROW_MIN_HEIGHT_PX,
} from '../styles';
import Menu from '@components/Menu';
import LanguageSelector from '@components/LanguageSelector';
import { IPrismicIndex, MenuData, PrismicFooter } from '../../types/prismic';
import type { PrismicDataEdge } from '@utils/pageDataUtils';
import { NavigationThemeContext } from '../context/NavigationThemeContext';
import { useI18n } from '../context/I18nContext';
import { getLocalizedPath } from '@utils/websiteUrlUtils';
import type { LocaleCode } from '@config/i18n';

const { breakpoints } = theme;

type Props = {
  navData?: PrismicDataEdge | null;
  footerData?: PrismicDataEdge | null;
};

const pickEdgeByLocale = (
  edges: Array<{ node: { lang?: string; data: unknown } }>,
  locale: string
): { node: { data: unknown } } | undefined => {
  if (!edges?.length) return undefined;
  const normalized = locale.toLowerCase().replace('_', '-');
  const match = edges.find((e) => (e.node.lang ?? '').toLowerCase().replace('_', '-') === normalized);
  return match ?? edges[0];
};

const Navigation: React.FC<Props> = ({ navData: navDataProp, footerData: footerDataProp }) => {
  const { t, locale } = useI18n();
  const currentLocale = locale as LocaleCode;
  const [showHeader, setShowHeader] = useState(true);
  const staticData = useStaticQuery(query);
  // For non-Prismic locales, mergedNavData/mergedFooterData come from pageContext.
  // Fallback: pick static query edge matching current locale (scales when multiple Prismic locales exist).
  const navEdge = navDataProp?.edges?.[0] ?? pickEdgeByLocale(staticData.data?.edges ?? [], currentLocale);
  const navData = (navEdge?.node?.data ?? {}) as IPrismicIndex;
  const footerEdge = footerDataProp?.edges?.[0] ?? pickEdgeByLocale(staticData.prismicFooterData?.edges ?? [], currentLocale);
  const effectiveFooterData: PrismicDataEdge = footerDataProp ?? (footerEdge ? { edges: [{ node: { data: footerEdge.node.data } }] } : staticData.prismicFooterData);
  const { logo, logo_dark, body } = navData;

  const menuData: MenuData = { body };

  const footerData: PrismicFooter = (effectiveFooterData?.edges?.[0]?.node?.data ?? {}) as PrismicFooter;

  const { navigationTheme } = useContext(NavigationThemeContext);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hideHeader = urlParams.get('hideHeader');
    if (hideHeader === 'true') {
      setShowHeader(false);
    }
  }, []);

  return (
    <Container style={{ display: showHeader ? 'flex' : 'none' }}>
      <LogoWrapper>
        <Link to={getLocalizedPath('/', currentLocale)}>
          {navigationTheme === 'dark' ? (
            <Logo src={logo_dark?.url} alt={t('common:logo.alt')} />
          ) : (
            <Logo src={logo?.url} alt={t('common:logo.alt')} />
          )}
        </Link>
      </LogoWrapper>
      <RightSection>
        <Menu menuData={menuData} footerData={footerData} />
        <LanguageSelector variant="desktop" />
      </RightSection>
    </Container>
  );
};

export default Navigation;

const LogoWrapper = styled.div`
  z-index: 1;
  @media screen and (max-width: calc(${breakpoints.smallLaptop} - 1px)) {
    display: flex;
    align-items: center;
    min-height: ${MOBILE_LOGO_ROW_MIN_HEIGHT_PX}px;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;

  @media screen and (max-width: calc(${breakpoints.smallLaptop} - 1px)) {
    height: 0;
    width: 0;
    overflow: visible;
    position: relative;
  }

  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    gap: 32px;
  }
`;

/** Banner landmark: screen readers and the Markdown build (which skips it) know this is site chrome. */
const Container = styled.header`
  box-sizing: border-box;
  ${siteHeaderTopSafePadding}
  padding-left: 24px;
  padding-right: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin-top: 0;
  margin-left: auto;
  margin-right: auto;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  z-index: 999;
  @media screen and (max-width: calc(${breakpoints.smallLaptop} - 1px)) {
    padding-bottom: ${SITE_HEADER_TOP_MIN_PADDING_PX}px;
  }
  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    padding-left: 0;
    padding-right: 0;
    max-width: 1200px;
  }
`;

const Logo = styled.img`
  width: 150px;
`;

const query = graphql`
  query NavigationData {
    prismicFooterData: allPrismicIndex {
      ...FooterFieldsFragment
    }
    data: allPrismicIndex {
      edges {
        node {
          lang
          data {
            logo {
              url
              alt
            }
            logo_dark {
              url
              alt
            }
            body {
              ... on PrismicIndexDataBodyGroupOfLinks {
                items {
                  inner_link
                  inner_link_label
                  inner_link_id
                  hidden
                }
                primary {
                  link
                  link_label
                  link_is_external
                  link_is_button
                  link_id
                }
              }
            }
            copyright_text
          }
        }
      }
    }
  }
`;
